import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { FreightService } from '../work-orders/freight/freight.service';
import { WorkOrderService } from '../work-orders/work-order-details/work-order.service';

type LabelBrand = 'FBI' | 'ULAAP';
type LabelSource = 'trip' | 'workOrder';

interface PackageLabelHeader {
  trip_list_uuid?: string;
  trip_number?: string;
  date?: string;
  asn_number?: string;
  trailer_id?: string;
  warehouse_reference?: string;
  note?: string;
  lading_uuid?: string;
  lading_number?: string;
  alternate_reference?: string;
  customer_po?: string;
  customer_name?: string;
  [key: string]: any;
}

interface PackageLabelPackage {
  package_uuid: string;
  package_name?: string;
  contact_organization?: string;
  store_number?: string;
  note?: string;
  product_count?: number;
  product_model_number?: string;
  product_model?: string;
  item_number?: string;
  pallet_serial_number?: string;
  shipping_length?: number | string;
  shipping_width?: number | string;
  shipping_height?: number | string;
  shipping_weight?: number | string;
  product_description?: string;
  package_description?: string;
  item_description?: string;
  description?: string;
  product_name?: string;
  stackable?: boolean | string | number | null;
  [key: string]: any;
}

interface PackageLabelProduct {
  package_uuid: string;
  product_serial_number?: string;
  product_description?: string;
  package_description?: string;
  item_description?: string;
  description?: string;
  [key: string]: any;
}

interface PackageLabelsResponse {
  success: boolean;
  message?: string;
  header?: PackageLabelHeader;
  packages?: PackageLabelPackage[];
  products?: PackageLabelProduct[];
}

interface LabelViewModel {
  uuid: string;
  name: string;
  customer: string;
  store: string;
  note: string;
  count: number | string;
  intakeDate: string;
  model: string;
  serial: string;
  dims: { l: string | number; w: string | number; h: string | number };
  weight: string | number;
  stackable: PackageLabelPackage['stackable'];
  desc: string;
}

@Component({
  selector: 'app-trip-package-labels',
  templateUrl: './trip-package-labels.component.html',
  styleUrls: ['./trip-package-labels.component.scss']
})
export class TripPackageLabelsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('qrTarget') qrTargets!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('modelBarcode') modelBarcodes!: QueryList<ElementRef<SVGElement>>;
  @ViewChildren('serialBarcode') serialBarcodes!: QueryList<ElementRef<SVGElement>>;

  source: LabelSource = 'trip';
  sourceUuid = '';
  header: PackageLabelHeader = {};
  packages: PackageLabelPackage[] = [];
  products: PackageLabelProduct[] = [];
  labels: LabelViewModel[] = [];
  selected = new Set<string>();
  brand: LabelBrand = 'FBI';
  loading = false;
  errorMessage = '';
  private readonly printBodyClass = 'trip-package-label-print';

  readonly brands: Record<LabelBrand, { displayName: string; logoSrc: string }> = {
    FBI: { displayName: 'The FBI Group', logoSrc: 'assets/fbi_logo_side_split_difference_small.png' },
    ULAAP: { displayName: 'ULAAP', logoSrc: 'assets/ulaap_logo.png' }
  };

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private freightService: FreightService,
    private workOrderService: WorkOrderService
  ) {}

  ngOnInit(): void {
    document.body.classList.add(this.printBodyClass);
    this.source = this.route.snapshot.data['labelSource'] === 'workOrder' ? 'workOrder' : 'trip';
    this.sourceUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.loadLabels();
  }

  ngAfterViewInit(): void {
    this.qrTargets.changes.subscribe(() => this.renderMachineCodes());
    this.modelBarcodes.changes.subscribe(() => this.renderMachineCodes());
    this.serialBarcodes.changes.subscribe(() => this.renderMachineCodes());
  }

  ngOnDestroy(): void {
    document.body.classList.remove(this.printBodyClass);
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  get allSelected(): boolean {
    return this.labels.length > 0 && this.selected.size === this.labels.length;
  }

  get batchInfo(): string {
    const pieces = [`${this.labels.length} labels`];

    if (this.source === 'workOrder') {
      const reference = this.header.alternate_reference || this.header.lading_number;
      if (reference) pieces.push(`Work order ${reference}`);
      if (this.header.customer_po) pieces.push(`PO ${this.header.customer_po}`);
    } else {
      if (this.header.trip_number) pieces.push(`Trip ${this.header.trip_number}`);
      const po = this.header.asn_number || this.header.warehouse_reference;
      if (po) pieces.push(`PO ${po}`);
      if (this.header.trailer_id) pieces.push(`Trailer ${this.header.trailer_id}`);
    }

    return pieces.join(' / ');
  }

  get pageEyebrow(): string {
    return this.source === 'workOrder' ? 'Work Orders / Package Labels' : 'Inventory / Package Labels';
  }

  get pageTitle(): string {
    return this.source === 'workOrder' ? 'Warehouse Package Labels' : 'Direct Intake Labels';
  }

  get backTitle(): string {
    return this.source === 'workOrder' ? 'Back to work order' : 'Back to package operations';
  }

  get emptyMessage(): string {
    return this.source === 'workOrder'
      ? 'No package labels found for this work order.'
      : 'No package labels found for this trip.';
  }

  get currentBrand() {
    return this.brands[this.brand] || this.brands.FBI;
  }

  loadLabels(): void {
    if (!this.sourceUuid) {
      this.errorMessage = this.source === 'workOrder' ? 'Missing work order UUID.' : 'Missing trip UUID.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    if (this.source === 'workOrder') {
      this.loadWorkOrderLabels();
      return;
    }

    this.http.get<PackageLabelsResponse>('/api/intake/package_labels.php', {
      params: {
        request_type: 'trip_label_detail',
        trip_list_uuid: this.sourceUuid
      }
    }).subscribe({
      next: response => {
        this.applyLabelResponse(response, 'Unable to load labels.');
      },
      error: error => {
        console.error(error);
        this.loading = false;
        this.errorMessage = 'Unable to load package labels.';
      }
    });
  }

  private loadWorkOrderLabels(): void {
    forkJoin({
      details: this.workOrderService.getWorkOrderDetails(this.sourceUuid),
      freight: this.freightService.load(this.sourceUuid)
    }).subscribe({
      next: ({ details, freight }) => {
        const lading = details?.lading;
        if (!lading || !freight?.success) {
          this.applyLabelResponse(
            { success: false, message: 'Unable to load work order packages.' },
            'Unable to load work order packages.'
          );
          return;
        }

        const detailPackages = new Map(
          (lading.packages || []).map(pkg => [pkg.package_uuid, pkg])
        );
        const detailProducts = new Map(
          (lading.products || []).map(product => [product.product_uuid, product])
        );

        const products: PackageLabelProduct[] = [];
        const packages: PackageLabelPackage[] = (freight.packages || []).map(pkg => {
          const detailPackage = detailPackages.get(pkg.package_uuid);
          const packageProducts = (pkg.products || []).map(product => {
            const detailProduct = detailProducts.get(product.product_uuid);
            const normalized = {
              ...detailProduct,
              ...product,
              package_uuid: pkg.package_uuid
            } as PackageLabelProduct;
            products.push(normalized);
            return normalized;
          });
          const firstProduct = packageProducts[0];

          return {
            ...detailPackage,
            ...pkg,
            package_uuid: pkg.package_uuid,
            package_name: detailPackage?.package_name || (pkg as any).package_name || '',
            contact_organization: detailPackage?.contact_organization || lading.customer_name || '',
            store_number: detailPackage?.store_number || (pkg as any).store_number || firstProduct?.['store_number'],
            note: detailPackage?.note || lading.load_special_info || '',
            product_count: detailPackage?.product_count || packageProducts.length,
            product_model_number: detailPackage?.item_number
              || firstProduct?.['product_model_number']
              || firstProduct?.['product_code']
              || '',
            pallet_serial_number: detailPackage?.pallet_serial_number
              || firstProduct?.['pallet_serial_number']
              || '',
            shipping_length: detailPackage?.length || (pkg as any).length,
            shipping_width: detailPackage?.width || (pkg as any).width,
            shipping_height: detailPackage?.height || (pkg as any).height,
            shipping_weight: detailPackage?.weight || (pkg as any).weight,
            stackable: detailPackage?.stackable ?? firstProduct?.['stackable'],
            product_description: this.firstText(
              detailPackage?.description,
              firstProduct?.product_description,
              firstProduct?.description
            )
          };
        });

        this.applyLabelResponse({
          success: true,
          header: {
            lading_uuid: lading.lading_uuid,
            lading_number: lading.lading_number,
            alternate_reference: lading.alternate_reference,
            customer_po: lading.customer_po || '',
            customer_name: lading.customer_name,
            trip_number: lading.trip_number,
            date: lading.intake_date || lading.created || '',
            note: lading.load_special_info || ''
          },
          packages,
          products
        }, 'Unable to load work order labels.');
      },
      error: error => {
        console.error(error);
        this.loading = false;
        this.errorMessage = 'Unable to load work order package labels.';
      }
    });
  }

  private applyLabelResponse(response: PackageLabelsResponse, fallbackMessage: string): void {
    this.loading = false;
    if (!response?.success) {
      this.errorMessage = response?.message || fallbackMessage;
      return;
    }

    this.header = response.header || {};
    this.packages = response.packages || [];
    this.products = response.products || [];
    this.labels = this.packages.map(pkg => this.toLabel(pkg));
    this.selected.clear();
    this.scheduleMachineCodeRender();
  }

  toggleLabel(uuid: string): void {
    if (this.selected.has(uuid)) {
      this.selected.delete(uuid);
    } else {
      this.selected.add(uuid);
    }
  }

  toggleAll(checked: boolean): void {
    this.selected.clear();
    if (checked) {
      for (const label of this.labels) {
        this.selected.add(label.uuid);
      }
    }
  }

  isSelected(uuid: string): boolean {
    return this.selected.has(uuid);
  }

  isLastPrintable(uuid: string): boolean {
    const printable = this.selected.size
      ? this.labels.filter(label => this.selected.has(label.uuid))
      : this.labels;

    return printable.length > 0 && printable[printable.length - 1].uuid === uuid;
  }

  setBrand(brand: string): void {
    this.brand = brand === 'ULAAP' ? 'ULAAP' : 'FBI';
    this.scheduleMachineCodeRender();
  }

  printSelected(): void {
    if (!this.selected.size) {
      this.toggleAll(true);
    }
    window.setTimeout(() => window.print(), 0);
  }

  backToSource(): void {
    const commands = this.source === 'workOrder'
      ? ['/work_order_details', this.sourceUuid]
      : ['/trip_package_operations'];
    this.router.navigate(commands);
  }

  trackByLabel(_: number, label: LabelViewModel): string {
    return label.uuid;
  }

  stackableText(value: LabelViewModel['stackable']): string {
    const isStack = value === true || value === 'true' || value === 1 || value === '1';
    return isStack ? 'Yes' : 'No';
  }

  private toLabel(pkg: PackageLabelPackage): LabelViewModel {
    const prod = this.products.find(item => item.package_uuid === pkg.package_uuid);
    const model = this.firstText(
      pkg.product_model_number,
      pkg.product_model,
      pkg.item_number,
      prod?.['product_model_number'],
      prod?.['product_code']
    );
    const desc = this.firstText(
      pkg.product_description,
      pkg.package_description,
      pkg.item_description,
      pkg['product_desc'],
      pkg['package_desc'],
      pkg['item_desc'],
      pkg['model_description'],
      pkg['customer_product_description'],
      pkg.description,
      pkg.product_name,
      prod?.product_description,
      prod?.package_description,
      prod?.item_description,
      prod?.['product_desc'],
      prod?.['item_desc'],
      prod?.['model_description'],
      prod?.description
    );
    const headerDate = this.header.date || '';

    return {
      uuid: pkg.package_uuid,
      name: pkg.package_name || '',
      customer: pkg.contact_organization || this.header['customer_name'] || 'Unknown',
      store: pkg.store_number ? `Store ${pkg.store_number}` : '',
      note: pkg.note || this.header.note || '',
      count: pkg.product_count || 0,
      intakeDate: this.formatLabelDate(headerDate),
      model,
      serial: prod?.product_serial_number || pkg.pallet_serial_number || '',
      dims: {
        l: this.cleanNum(pkg.shipping_length),
        w: this.cleanNum(pkg.shipping_width),
        h: this.cleanNum(pkg.shipping_height)
      },
      weight: this.cleanNum(pkg.shipping_weight),
      stackable: pkg.stackable,
      desc
    };
  }


  private firstText(...values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return '';
  }

  private formatLabelDate(value: string): string {
    if (!value) return new Date().toISOString().slice(0, 16).replace('T', ' ');
    return String(value).replace('T', ' ').substring(0, 16);
  }

  private cleanNum(value: unknown): string | number {
    if (value === null || value === undefined || value === '') return '';
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : String(value);
  }

  private scheduleMachineCodeRender(): void {
    window.setTimeout(() => this.renderMachineCodes(), 0);
  }

  private async renderMachineCodes(): Promise<void> {
    if (!this.labels.length) return;

    try {
      await Promise.all([this.ensureQRCodeLib(), this.ensureJsBarcodeLib()]);
    } catch (error) {
      console.warn('Unable to load label barcode libraries', error);
      return;
    }

    const JsBarcode = (window as any).JsBarcode;

    this.qrTargets?.forEach(ref => this.renderQRCode(ref.nativeElement));

    this.modelBarcodes?.forEach(ref => this.renderBarcode(JsBarcode, ref.nativeElement));
    this.serialBarcodes?.forEach(ref => this.renderBarcode(JsBarcode, ref.nativeElement));
  }

  private renderBarcode(JsBarcode: any, svg: SVGElement): void {
    const val = svg.dataset['val'];
    if (!val || svg.dataset['rendered'] === val) return;

    JsBarcode(svg, val, {
      format: 'CODE128',
      width: 1.5,
      height: 35,
      displayValue: false,
      margin: 0
    });
    svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
    svg.dataset['rendered'] = val;
  }

  private renderQRCode(el: HTMLElement): void {
    const uuid = el.dataset['uuid'];
    if (!uuid || el.dataset['rendered'] === uuid) return;

    const QRCode = (window as any).QRCode;
    const payload = `package,${uuid}`;
    el.innerHTML = '';

    try {
      if (typeof QRCode === 'function') {
        new QRCode(el, { text: payload, width: 112, height: 112 });
        el.dataset['rendered'] = uuid;
        return;
      }

      if (QRCode && typeof QRCode.toCanvas === 'function') {
        const canvas = document.createElement('canvas');
        QRCode.toCanvas(canvas, payload, { width: 112, margin: 0 }, (error: unknown) => {
          if (error) {
            console.error(error);
            return;
          }
          el.innerHTML = '';
          el.appendChild(canvas);
          el.dataset['rendered'] = uuid;
        });
        return;
      }

      if (QRCode && typeof QRCode.toDataURL === 'function') {
        QRCode.toDataURL(payload, { width: 112, margin: 0 }, (error: unknown, url: string) => {
          if (error) {
            console.error(error);
            return;
          }
          const img = document.createElement('img');
          img.src = url;
          img.alt = 'Package QR code';
          el.innerHTML = '';
          el.appendChild(img);
          el.dataset['rendered'] = uuid;
        });
        return;
      }

      throw new Error('No compatible QRCode renderer loaded.');
    } catch (error) {
      console.error('Unable to render package QR code', error);
      el.dataset['rendered'] = '';
    }
  }

  private ensureQRCodeLib(): Promise<void> {
    if (this.hasCompatibleQRCodeLib()) return Promise.resolve();
    return this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js', true).then(() => {
      if (!this.hasCompatibleQRCodeLib()) {
        return this.loadScript(`https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js?tpl=${Date.now()}`, true);
      }
      return undefined;
    });
  }

  private hasCompatibleQRCodeLib(): boolean {
    const QRCode = (window as any).QRCode;
    return typeof QRCode === 'function' || typeof QRCode?.toCanvas === 'function' || typeof QRCode?.toDataURL === 'function';
  }

  private ensureJsBarcodeLib(): Promise<void> {
    if ((window as any).JsBarcode) return Promise.resolve();
    return this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jsbarcode/3.11.5/JsBarcode.all.min.js');
  }

  private loadScript(src: string, force = false): Promise<void> {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && !force) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Unable to load ${src}`));
      document.head.appendChild(script);
    });
  }
}
